import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { generateComplianceAuditPackage } from '../../../utils/exportUtils';
import { useData } from '../../../contexts/DataContext';

const { FiShield, FiDownload, FiCheckCircle, FiFileText, FiAlertCircle, FiLock } = FiIcons;

export default function ComplianceAuditPackage() {
  const { currentCompany, reports, acknowledgements } = useData();
  const [isGenerating, setIsGenerating] = useState(false);

  const companyReports = reports.filter(r => r.company_id === currentCompany?.id);
  
  const stats = {
    hmsCount: companyReports.filter(r => r.type === 'hms' || r.type === 'ruh').length,
    whistleCount: companyReports.filter(r => r.type === 'varsling').length,
    hrCount: companyReports.filter(r => r.category?.toLowerCase().includes('hr') || r.category?.toLowerCase().includes('personell')).length,
    financeCount: companyReports.filter(r => r.category?.toLowerCase().includes('økonomi')).length
  };

  const procedures = currentCompany?.documents || [];

  const handleExport = async () => {
    setIsGenerating(true);
    try {
      // Simulate data gathering if needed
      await new Promise(resolve => setTimeout(resolve, 1000));
      generateComplianceAuditPackage(currentCompany, stats, procedures, acknowledgements);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
            <SafeIcon icon={FiShield} className="text-xl" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Compliance Audit Package</h3>
            <p className="text-sm text-slate-500 font-medium">Samsvarsdokumentasjon for revisjon og tilsyn</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={isGenerating}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-sm disabled:opacity-50"
        >
          {isGenerating ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <SafeIcon icon={FiDownload} />
          )}
          {isGenerating ? 'Genererer...' : 'Last ned full pakke'}
        </button>
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column: Status Indicators */}
        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Samsvarsstatus</h4>
            
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-100 rounded-lg">
              <SafeIcon icon={FiCheckCircle} className="text-green-600 text-lg mt-0.5" />
              <div>
                <p className="text-sm font-bold text-green-900">Sikker Varslingskanal</p>
                <p className="text-xs text-green-700">Aktiv, kryptert og GDPR-kompatibel.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <SafeIcon icon={FiFileText} className="text-blue-600 text-lg mt-0.5" />
              <div>
                <p className="text-sm font-bold text-blue-900">Rutiner & Prosedyrer</p>
                <p className="text-xs text-blue-700">{procedures.length} aktive dokumenter inkludert i pakken.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100 rounded-lg">
              <SafeIcon icon={FiLock} className="text-slate-600 text-lg mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900">Audit Trail</p>
                <p className="text-xs text-slate-700">Full sporbarhet på alle saksbehandlingsskritt.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Info/Warning */}
        <div className="bg-slate-900 rounded-xl p-6 text-white relative overflow-hidden">
          <div className="relative z-10">
            <h4 className="text-lg font-bold mb-2">Viktig for revisjon</h4>
            <p className="text-slate-400 text-sm mb-4 leading-relaxed">
              Dette dokumentet er generert som et bevis på at deres organisasjon oppfyller lovkravene til varslingsrutiner. 
              Ved tilsyn fra Arbeidstilsynet eller ekstern revisjon, er dette pakken dere skal fremvise.
            </p>
            <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold bg-yellow-400/10 p-2 rounded border border-yellow-400/20">
              <SafeIcon icon={FiAlertCircle} />
              <span>Inneholder aggregerte data - ingen personopplysninger blottstilles.</span>
            </div>
          </div>
          {/* Decorative shield background */}
          <SafeIcon icon={FiShield} className="absolute -bottom-4 -right-4 text-white/5 text-9xl transform -rotate-12" />
        </div>
      </div>
    </div>
  );
}